// The substrate (02-DESIGN/03-console/03-the-signed-out-site.md § the
// visual): under the closing words, a low plane of points seen from a shallow
// angle, joined in a lattice, a few of them in the teal. Seeded, so the same
// plane on every visit; drawn once, and again only when its box changes size
// — it never moves, so the reduced-motion preference has nothing to still.
// Ported from the prototype the owner approved.

import { at, fit, inkOf, mulberry32, type Vec3 } from "./draw.ts";

const COLS = 30;
const ROWS = 9;
/** The points in the teal. */
const MARKS = [41, 97, 133, 188, 231];

/** buildSubstrate is the plane's points: u across, v into the distance, and a height. */
export function buildSubstrate(seed = 3): readonly Vec3[] {
  const rand = mulberry32(seed);
  const grid: Vec3[] = [];
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      const u = c / (COLS - 1);
      const v = r / (ROWS - 1);
      const h =
        0.18 * Math.sin(u * 7.1 + v * 2.3) + 0.12 * Math.sin(u * 13.7 - v * 5.1) + (rand() - 0.5) * 0.08;
      grid.push([u + (rand() - 0.5) * 0.012, v + (rand() - 0.5) * 0.03, h]);
    }
  }
  return grid;
}

/** drawSubstrate draws the plane on a canvas, redrawing it on a resize; it returns what stops that. */
export function drawSubstrate(canvas: HTMLCanvasElement): () => void {
  const ctx = canvas.getContext("2d");
  if (ctx === null) {
    return () => undefined;
  }
  const grid = buildSubstrate();
  const ink = inkOf(canvas);

  const draw = () => {
    const { w: W, h: H, dpr } = fit(canvas);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    // A plane seen from a low angle: the far rows narrower and higher.
    const P = grid.map((g): Vec3 => {
      const depth = 1 - g[1];
      const f = 0.55 + 0.45 * (1 - depth);
      return [W * 0.5 + (g[0] - 0.5) * W * f, H * 0.9 - g[1] * H * 0.62 - g[2] * H * 0.5 * f, depth];
    });
    ctx.lineWidth = 0.8;
    const line = (a: Vec3, b: Vec3) => {
      const al = 0.12 + 0.3 * (1 - (a[2] + b[2]) / 2);
      ctx.strokeStyle = `rgba(${ink.mesh},${al.toFixed(3)})`;
      ctx.beginPath();
      ctx.moveTo(a[0], a[1]);
      ctx.lineTo(b[0], b[1]);
      ctx.stroke();
    };
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const i = r * COLS + c;
        if (c < COLS - 1) {
          line(at(P, i), at(P, i + 1));
        }
        if (r < ROWS - 1) {
          line(at(P, i), at(P, i + COLS));
        }
        if (c < COLS - 1 && r < ROWS - 1 && (r + c) % 2 === 0) {
          line(at(P, i), at(P, i + COLS + 1));
        }
      }
    }
    const dot = (p: Vec3, r: number, rgb: string, a: number) => {
      ctx.beginPath();
      ctx.arc(p[0], p[1], r, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${rgb},${a.toFixed(3)})`;
      ctx.fill();
    };
    for (const p of P) {
      const nr = 1 - p[2];
      dot(p, 0.9 + 1.3 * nr, ink.mesh, 0.3 + 0.5 * nr);
    }
    for (const m of MARKS) {
      dot(at(P, m % P.length), 2.4, ink.teal, 0.85);
    }
  };

  draw();
  const ro = typeof ResizeObserver === "function" ? new ResizeObserver(draw) : undefined;
  ro?.observe(canvas);
  return () => ro?.disconnect();
}
